/**
 * graphicFontSubstitution.ts — PLAT-11466 (epic PLAT-11407)
 *
 * Font correction for TRANSLATED COMPOSITION TEXT (nametags, lower-thirds,
 * title cards, pop-up overlays) — the graphics counterpart to the caption
 * substitution in `captionFonts.ts`.
 *
 * The epic fixed captions; graphics kept the same defect. Switching the
 * caption language rewrites a composition's text to the target language
 * (`projectCompositionTranslations`) but never touched `font_family`, so a
 * Latin-only face was asked to render Japanese and produced tofu — confirmed
 * in QA on a Text Pop Up overlay set in DINNextLTPro.
 *
 * The rule differs from captions on purpose. A caption's font is ours to
 * choose, so an UNKNOWN family is assumed Latin-only and substituted. A
 * composition's font is the customer's brand, so silence is the safer default:
 * we substitute only where we can show the family cannot render the script.
 * Readability still wins wherever we do know — an unreadable brand font
 * communicates nothing at all.
 *
 *   covers the script (catalog)      → keep, always
 *   known family, lacks the script   → substitute
 *   brand font PROBED as lacking it  → substitute (PLAT-11412's cmap data)
 *   nothing known about the family   → keep (never guess against a brand)
 *
 * Pure — no React, no MobX, no IO.
 */

import {
  fontCoversScript,
  getCaptionFontSubstitute,
  isKnownCaptionFontFamily,
  normalizeCaptionFontFamilyKey,
} from './captionFonts';
import { resolveCaptionScript } from './captionLanguage';
import { familyLacksScript } from './fontScriptCoverage';

import type { FontScriptCoverage } from './fontScriptCoverage';

/** The only shape this module needs from a brand font variant. */
export interface GraphicFontVariantLike {
  scriptCoverage?: FontScriptCoverage;
}

/**
 * Brand-font coverage by the name that appears on a rendered element. Returns
 * `undefined` for a family we hold no variants for — deliberately distinct
 * from an empty array, so "not a brand font" and "a brand font we never
 * probed" both land on "keep".
 */
export type BrandFontVariantLookup = (
  fontFamily: string,
) => readonly GraphicFontVariantLike[] | undefined;

/**
 * The font a translated composition element should render in, or `null` to
 * keep what it has.
 */
export function getGraphicFontSubstitute(
  fontFamily: string | null | undefined,
  targetLanguage: string,
  brandVariants?: BrandFontVariantLookup,
): string | null {
  if (!fontFamily) return null;
  const script = resolveCaptionScript(targetLanguage);
  if (script === 'latin') return null;
  // A family the catalog vouches for is never touched — this is also what
  // stops an already-substituted element being re-substituted on the next
  // paint.
  if (fontCoversScript(fontFamily, script)) return null;

  const knownToLack =
    isKnownCaptionFontFamily(fontFamily) ||
    familyLacksScript(brandVariants?.(fontFamily) ?? [], script);
  if (!knownToLack) return null;

  return getCaptionFontSubstitute(fontFamily, script)?.font_family ?? null;
}

/**
 * Index a workspace's custom fonts for `getGraphicFontSubstitute`.
 *
 * Indexed by BOTH family name and PostScript name: `CustomFontsService`
 * registers each variant with the renderer under its `postScriptName`, so a
 * composition element's `font_family` is usually the PostScript name
 * ("DINNextLTPro-Regular"), not the family ("DIN Next LT Pro"). Looking up
 * only the family would silently find nothing and keep the tofu.
 *
 * Archived fonts are indexed too — an element can still reference one, and its
 * coverage is just as true.
 */
export function makeBrandFontVariantLookup(
  customFonts:
    | ReadonlyArray<{
        fontFamily?: string;
        postScriptName?: string;
        scriptCoverage?: FontScriptCoverage;
      }>
    | null
    | undefined,
): BrandFontVariantLookup {
  if (!customFonts?.length) return () => undefined;
  const byName = new Map<string, GraphicFontVariantLike[]>();
  const add = (name: string | undefined, font: GraphicFontVariantLike) => {
    const key = normalizeCaptionFontFamilyKey(name);
    if (!key) return;
    const existing = byName.get(key);
    if (existing) existing.push(font);
    else byName.set(key, [font]);
  };
  for (const font of customFonts) {
    add(font.fontFamily, font);
    if (
      normalizeCaptionFontFamilyKey(font.postScriptName) !==
      normalizeCaptionFontFamilyKey(font.fontFamily)
    ) {
      add(font.postScriptName, font);
    }
  }
  return (fontFamily: string) =>
    byName.get(normalizeCaptionFontFamilyKey(fontFamily));
}
